import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Toaster, toast } from "react-hot-toast";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import { Dashboard } from "./views/Dashboard";
import { AppsList } from "./views/AppsList";
import { AppDetail } from "./views/AppDetail";
import { EmbeddedScripts } from "./views/EmbeddedScripts";
import { Card, Button, Input, IconUpload } from "./components/ui";
import { AVAILABLE_SCOPES } from "./constants";
import developerAPI from "./api/developer-api";

const AppContext = createContext(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within App");
  return ctx;
}

function App() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    (async () => {
      const ok = await developerAPI.isAuthenticated();
      if (!ok) {
        developerAPI.redirectToLogin();
        return;
      }
      const userInfo = await developerAPI.getUserFromToken();
      if (userInfo) setUser(userInfo);
    })();
  }, []);

  const loadApps = useCallback(async () => {
    try {
      setLoading(true);
      const appsList = await developerAPI.listApps();
      setApps(appsList);
    } catch (err) {
      console.error("Failed to load apps:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (await developerAPI.isAuthenticated()) loadApps();
    })();
  }, [loadApps]);

  const value = { user, apps, loading, loadApps };

  return (
    <AppContext.Provider value={value}>
      <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="apps" element={<AppsList />} />
            <Route path="apps/new" element={<CreateAppScreen />} />
            <Route path="apps/:appId" element={<AppDetail />} />
            <Route path="apps/:appId/scripts/new" element={<EmbeddedScripts />} />
            <Route path="apps/:appId/scripts/:scriptId" element={<EmbeddedScripts />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppContext.Provider>
  );
}

function CreateAppScreen() {
  const navigate = useNavigate();
  const { loadApps, user } = useApp();
  const [appName, setAppName] = useState("");
  const [description, setDescription] = useState("");
  const [homepageUrl, setHomepageUrl] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [selectedScopes, setSelectedScopes] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdSecret, setCreatedSecret] = useState(null);
  const [createdApp, setCreatedApp] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);

  // Revoke object URL on unmount or when preview changes to avoid memory leaks
  useEffect(() => {
    return () => {
      if (iconPreviewUrl) URL.revokeObjectURL(iconPreviewUrl);
    };
  }, [iconPreviewUrl]);

  const handleIconSelect = async (file) => {
    if (iconPreviewUrl) URL.revokeObjectURL(iconPreviewUrl);
    setIconFile(file);
    setIconPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!appName.trim()) {
      setError("App name is required.");
      return;
    }
    if (!redirectUri.trim()) {
      setError("Redirect URI is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const userDisplayName = user ? [user.given_name, user.family_name].filter(Boolean).join(" ").trim() : "";
      const app = await developerAPI.createApp({
        app_name: appName.trim(),
        description: description.trim() || undefined,
        homepage_url: homepageUrl.trim() || undefined,
        redirect_uris: [redirectUri.trim()],
        scopes: selectedScopes,
        developer_name: userDisplayName || undefined,
      });
      if (iconFile && app?.id) {
        try {
          await developerAPI.uploadAppIcon(app.id, iconFile);
        } catch (iconErr) {
          console.warn("App created but icon upload failed:", iconErr);
          toast.error(iconErr.response?.data?.error || iconErr.message || "Icon upload failed. You can set it in app settings.");
        }
      }
      setCreatedSecret(app.client_secret || null);
      setCreatedApp(app);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to create app.");
    } finally {
      setSubmitting(false);
    }
  };

  if (createdApp && createdSecret) {
    return (
      <div className="p-8 max-w-6xl mx-auto h-full overflow-y-auto">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">App Created</h1>
        <Card>
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
              <p className="text-emerald-800 font-medium text-sm">Your app has been created successfully.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client ID</label>
              <div className="mt-1 bg-slate-50 border border-slate-200 p-3 rounded-md font-mono text-sm text-slate-700">{createdApp.client_id}</div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client Secret</label>
              <div className="mt-1 bg-slate-50 border border-slate-200 p-3 rounded-md font-mono text-sm text-slate-700 break-all">{createdSecret}</div>
              <p className="text-xs text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded border border-amber-100 inline-block">Copy this secret now — it won't be shown again.</p>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
              <Button variant="secondary" onClick={() => navigator.clipboard.writeText(createdSecret)}>Copy Secret</Button>
              <Button onClick={() => { loadApps(); navigate(`/apps/${createdApp.id}`); }}>Go to App</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto h-[95vh] flex flex-col overflow-hidden">
      <h1 className="text-3xl font-bold text-slate-900 mb-6 shrink-0">Create New App</h1>
      <Card noPadding fillHeight className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 items-stretch flex-1 min-h-0 overflow-hidden">
          {/* Left: icon, app name, description, homepage URL, redirect URI — tight spacing to fit without scroll */}
          <div className="space-y-3 min-h-0 overflow-y-auto pr-2 [&>div]:!mb-0">
            <IconUpload label="App Icon" value={iconPreviewUrl} onUpload={handleIconSelect} />
            <div>
              <Input compact label="App Name" placeholder="e.g. Analytics Sync" value={appName} onChange={(e) => setAppName(e.target.value)} />
              <p className="text-xs text-slate-500 -mt-1.5">This will be displayed to users on the consent screen.</p>
            </div>
            <Input compact label="Description" placeholder="What does this app do?" value={description} onChange={(e) => setDescription(e.target.value)} />
            <Input compact label="Homepage URL" placeholder="https://" value={homepageUrl} onChange={(e) => setHomepageUrl(e.target.value)} />
            <Input compact label="Redirect URI" placeholder="https://yourapp.com/callback" subtext="Where we send the OAuth authorization code." value={redirectUri} onChange={(e) => setRedirectUri(e.target.value)} />
          </div>

          {/* Right: scopes container — same height as left column; inner list scrollable */}
          <div className="flex flex-col min-h-[320px] lg:min-h-0">
            <label className="block text-slate-700 text-xs font-semibold mb-2 shrink-0">Scopes</label>
            <div className="flex-1 min-h-0 flex flex-col border border-slate-200 rounded-lg bg-slate-50/50 overflow-hidden">
              <p className="text-xs text-slate-500 px-3 py-2 bg-amber-50/80 border-b border-slate-200 shrink-0">👇 Scroll down to see all the scopes</p>
              <div className="grid grid-cols-1 gap-2 overflow-y-auto p-3 min-h-0 flex-1">
              {AVAILABLE_SCOPES.map((scope) => (
                <label key={scope.id} className="flex items-start space-x-3 p-3 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-all cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(scope.id)}
                    onChange={(e) => (e.target.checked ? setSelectedScopes((prev) => [...prev, scope.id]) : setSelectedScopes((prev) => prev.filter((s) => s !== scope.id)))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-grigora-600 focus:ring-grigora-500"
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-mono font-medium text-slate-800">{scope.id}</span>
                    <p className="text-xs text-slate-500 mt-0.5">{scope.description}</p>
                  </div>
                </label>
              ))}
              </div>
            </div>
          </div>
        </div>

          {error && <div className="mt-4 shrink-0 bg-red-50 border border-red-200 p-3 rounded-lg text-red-700 text-sm">{error}</div>}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 mt-4 shrink-0">
            <Button variant="secondary" onClick={() => navigate("/apps")}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}>{submitting ? "Creating..." : "Create App"}</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default App;
